import React, { useState, useEffect } from 'react';
import { Activity, Zap, Clock, Database, Wifi, AlertTriangle } from 'lucide-react';

const PerformanceDashboard = ({ enabled = false }) => {
  const [metrics, setMetrics] = useState({
    loadTime: 0,
    renderTime: 0,
    memoryUsage: 0,
    apiCalls: 0,
    errors: 0,
    bundleSize: 0
  });

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    // Monitor page load time
    const measureLoadTime = () => {
      const loadTime = performance.now();
      setMetrics(prev => ({ ...prev, loadTime: Math.round(loadTime) }));
    };

    // Monitor memory usage
    const measureMemory = () => {
      if (performance.memory) {
        const memoryUsage = Math.round(performance.memory.usedJSHeapSize / 1024 / 1024);
        setMetrics(prev => ({ ...prev, memoryUsage }));
      }
    };

    // Monitor API calls
    let apiCallCount = 0;
    const originalFetch = window.fetch;
    window.fetch = (...args) => {
      apiCallCount++;
      setMetrics(prev => ({ ...prev, apiCalls: apiCallCount }));
      return originalFetch(...args);
    };

    // Monitor errors
    let errorCount = 0;
    const handleError = () => {
      errorCount++;
      setMetrics(prev => ({ ...prev, errors: errorCount }));
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleError);

    // Measure initial load
    if (document.readyState === 'complete') {
      measureLoadTime();
    } else {
      window.addEventListener('load', measureLoadTime);
    }

    // Monitor memory every 5 seconds
    const memoryInterval = setInterval(measureMemory, 5000);

    // Toggle visibility with Ctrl+Shift+P
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        setIsVisible(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      clearInterval(memoryInterval);
      window.removeEventListener('load', measureLoadTime);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
      window.removeEventListener('keydown', handleKeyDown);
      window.fetch = originalFetch;
    };
  }, [enabled]);

  if (!enabled || !isVisible) return null;

  const getPerformanceStatus = () => {
    if (metrics.loadTime > 3000) return { status: 'poor', color: 'text-red-600', bg: 'bg-red-50' };
    if (metrics.loadTime > 1500) return { status: 'fair', color: 'text-yellow-600', bg: 'bg-yellow-50' };
    return { status: 'good', color: 'text-green-600', bg: 'bg-green-50' };
  };

  const { status, color, bg } = getPerformanceStatus();

  return (
    <div className="fixed top-4 right-4 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Performance Monitor</h3>
          <button
            onClick={() => setIsVisible(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>
        <p className="text-sm text-gray-600">Press Ctrl+Shift+P to toggle</p>
      </div>

      <div className="p-4 space-y-4">
        {/* Load Time */}
        <div className={`p-3 rounded-lg ${bg}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Load Time</span>
            </div>
            <span className={`text-sm font-bold ${color}`}>
              {metrics.loadTime}ms
            </span>
          </div>
          <div className="mt-1 text-xs text-gray-600">
            Status: {status.toUpperCase()}
          </div>
        </div>

        {/* Memory Usage */}
        <div className="p-3 rounded-lg bg-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Database className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium text-gray-700">Memory</span>
            </div>
            <span className="text-sm font-bold text-blue-600">
              {metrics.memoryUsage}MB
            </span>
          </div>
          <div className="mt-1 text-xs text-gray-600">
            {metrics.memoryUsage > 100 ? 'High usage' : 'Normal usage'}
          </div>
        </div>

        {/* API Calls */}
        <div className="p-3 rounded-lg bg-green-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Wifi className="w-4 h-4 text-green-500" />
              <span className="text-sm font-medium text-gray-700">API Calls</span>
            </div>
            <span className="text-sm font-bold text-green-600">
              {metrics.apiCalls}
            </span>
          </div>
        </div>

        {/* Errors */}
        <div className={`p-3 rounded-lg ${metrics.errors > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertTriangle className={`w-4 h-4 ${metrics.errors > 0 ? 'text-red-500' : 'text-gray-500'}`} />
              <span className="text-sm font-medium text-gray-700">Errors</span>
            </div>
            <span className={`text-sm font-bold ${metrics.errors > 0 ? 'text-red-600' : 'text-gray-600'}`}>
              {metrics.errors}
            </span>
          </div>
        </div>

        {/* Bundle Size */}
        <div className="p-3 rounded-lg bg-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Activity className="w-4 h-4 text-purple-500" />
              <span className="text-sm font-medium text-gray-700">Bundle Size</span>
            </div>
            <span className="text-sm font-bold text-purple-600">
              {metrics.bundleSize}KB
            </span>
          </div>
        </div>

        {/* Performance Tips */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Performance Tips</h4>
          <ul className="text-xs text-gray-600 space-y-1">
            {metrics.loadTime > 2000 && (
              <li>• Consider lazy loading heavy components</li>
            )}
            {metrics.memoryUsage > 100 && (
              <li>• Check for memory leaks in components</li>
            )}
            {metrics.apiCalls > 20 && (
              <li>• Consider batching API calls</li>
            )}
            {metrics.errors > 0 && (
              <li>• Fix JavaScript errors to improve performance</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PerformanceDashboard;
